import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Images, ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLIDE_MS = 4500;   // Time each photo stays visible
const FADE_MS  = 900;    // Cross-fade duration

/**
 * Fading carousel — one photo at a time, cross-fades between them.
 * Photos are stacked absolutely on top of each other; only the active
 * index has full opacity, the rest are 0. CSS transition handles the
 * fade, so no rAF / no timing jank.
 * Pauses (freezes on the current photo) while hovered or focused so a
 * curious user can inspect a specific one.
 */
export default function GalleryStrip({ churchCode, target, testId = "gallery-strip", label }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!churchCode) return;
    let cancelled = false;
    axios
      .get(`${API}/public/church/${churchCode}/gallery/recent?limit=12`)
      .then((r) => { if (!cancelled) setPhotos(r.data || []); })
      .catch(() => { if (!cancelled) setPhotos([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [churchCode]);

  // Auto-advance timer. Reset whenever photo count or active index
  // changes so pausing (hover) doesn't accumulate a queue of ticks.
  useEffect(() => {
    if (paused || photos.length < 2) return;
    const t = setTimeout(() => {
      setActive((i) => (i + 1) % photos.length);
    }, SLIDE_MS);
    return () => clearTimeout(t);
  }, [active, photos.length, paused]);

  const go = () => navigate(target);

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={go}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="w-full block text-left rounded-2xl overflow-hidden border border-stone-200 bg-white hover:border-stone-300 transition"
    >
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <div
          className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
          style={{ backgroundColor: "color-mix(in srgb, var(--tenant-primary) 12%, transparent)" }}
        >
          <Images className="w-4 h-4 tenant-text" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow tenant-text leading-none">{label || t("gallery")}</div>
          <div className="text-[11px] text-stone-500 line-clamp-1 mt-0.5">{t("galleryStripCta")}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-stone-400" />
      </div>

      <div
        className="relative w-full aspect-[16/9] bg-stone-100 overflow-hidden"
        data-testid={`${testId}-stage`}
      >
        {loading && (
          <div className="absolute inset-0 grid place-items-center text-xs text-stone-400">…</div>
        )}
        {!loading && photos.length === 0 && (
          <div
            className="absolute inset-0 grid place-items-center text-xs text-stone-400"
            data-testid={`${testId}-empty`}
          >
            {t("galleryEmptyStrip")}
          </div>
        )}
        {photos.map((p, i) => (
          <img
            key={p.id}
            src={p.image_url}
            alt={p.caption || ""}
            loading={i === 0 ? "eager" : "lazy"}
            data-testid={`${testId}-photo-${p.id}`}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity: i === active ? 1 : 0,
              transition: `opacity ${FADE_MS}ms ease-in-out`,
            }}
          />
        ))}

        {/* Bottom fade + optional caption of the active photo */}
        {photos[active]?.caption && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
            <p
              className="text-white text-xs font-serif-display line-clamp-2"
              style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
            >
              {photos[active].caption}
            </p>
          </div>
        )}

        {/* Tiny dot indicators — only when there are 2+ photos */}
        {photos.length > 1 && (
          <div
            className="absolute bottom-2 right-3 flex gap-1"
            data-testid={`${testId}-dots`}
          >
            {photos.map((p, i) => (
              <span
                key={p?.id || p?.url || `dot-${i}`}
                className="w-1.5 h-1.5 rounded-full transition-all"
                style={{
                  backgroundColor: i === active ? "#fff" : "rgba(255,255,255,0.45)",
                  transform: i === active ? "scale(1.15)" : "scale(1)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </button>
  );
}
